/** The scope a registered route opens in — always the person's current one, never an id from a proposal. */
export const HELP_ROUTE_PARAM = ["workspace", "organization"];
const VOICE_AGENT_SETUP_PATH = "/agents/new/$agentTemplateCode";
const VOICE_AGENT_PAGE_PATH = "/agents/$voiceAgentId";
/** A step of the new-agent wizard (the screen "New agent"). */
function voiceAgentSetupStep(place, screenName) {
    return { path: VOICE_AGENT_SETUP_PATH, param: ["workspace"], capability: "voice_agent.write", requiresSignIn: true, screenName, within: "agent_setup", place };
}
/** A tab of an agent's page (the screen "Agent"). */
function voiceAgentTab(place, screenName, capability = "conversation.read") {
    return { path: VOICE_AGENT_PAGE_PATH, param: ["workspace"], capability, requiresSignIn: true, screenName, within: "agent", place };
}
export const HELP_ROUTE_REGISTRY = {
    // ── the way-in pages (outside the shell; the portal's anonymous mode) ──────────────────────
    "sign_up": {
        path: "/sign-up",
        param: [],
        capability: null,
        requiresSignIn: false,
        screenName: { en: "Create your account", el: "Δημιουργία λογαριασμού" },
    },
    "log_in": {
        path: "/log-in",
        param: [],
        capability: null,
        requiresSignIn: false,
        screenName: { en: "Log in", el: "Σύνδεση" },
    },
    // ── the open workspace: Overview ───────────────────────────────────────────────────────────
    "dashboard": {
        path: "/dashboard",
        param: ["workspace"],
        capability: null,
        requiresSignIn: true,
        screenName: { en: "Dashboard", el: "Πίνακας ελέγχου" },
    },
    "agents": {
        path: "/agents",
        param: ["workspace"],
        // OPERATIONAL_READ_ROLE: every role but the Billing preset — exactly `conversation.read`'s roles.
        capability: "conversation.read",
        requiresSignIn: true,
        screenName: { en: "Agents", el: "Πράκτορες" },
    },
    "agents.new": {
        path: "/agents/new",
        param: ["workspace"],
        capability: "voice_agent.write",
        requiresSignIn: true,
        screenName: { en: "Templates", el: "Πρότυπα" },
    },
    "live_activity": {
        path: "/live",
        param: ["workspace"],
        capability: "conversation.read",
        requiresSignIn: true,
        screenName: { en: "Live activity", el: "Ζωντανή δραστηριότητα" },
    },
    "campaigns.new": {
        path: "/campaigns/new",
        param: ["workspace"],
        capability: "campaign.operate",
        requiresSignIn: true,
        screenName: { en: "New campaign", el: "Νέα καμπάνια" },
    },
    // ── the open workspace: Channels ───────────────────────────────────────────────────────────
    "contact_lists": {
        path: "/contact-lists",
        param: ["workspace"],
        // routeRole owner, admin, member, campaign_manager — exactly `contact_list.manage`'s roles.
        capability: "contact_list.manage",
        requiresSignIn: true,
        screenName: { en: "Contact lists", el: "Λίστες επαφών" },
    },
    "numbers": {
        path: "/numbers",
        param: ["workspace"],
        capability: "conversation.read",
        requiresSignIn: true,
        screenName: { en: "Numbers & SIP", el: "Αριθμοί και SIP" },
    },
    "messaging": {
        path: "/messaging",
        param: ["workspace"],
        capability: "conversation.read",
        requiresSignIn: true,
        screenName: { en: "Messaging", el: "Μηνύματα" },
    },
    "messaging.web_chat": {
        path: "/messaging/web_chat",
        param: ["workspace"],
        capability: "conversation.read",
        requiresSignIn: true,
        screenName: { en: "Web chat", el: "Διαδικτυακή συνομιλία" },
    },
    // ── the open workspace: Account ────────────────────────────────────────────────────────────
    "settings": {
        path: "/settings",
        param: ["workspace"],
        capability: null,
        requiresSignIn: true,
        screenName: { en: "Settings", el: "Ρυθμίσεις" },
    },
    "compliance": {
        path: "/compliance",
        param: ["workspace"],
        // routeRole owner, admin — exactly `compliance.operator_access`'s roles.
        capability: "compliance.operator_access",
        requiresSignIn: true,
        screenName: { en: "Compliance", el: "Συμμόρφωση" },
    },
    "do_not_call": {
        path: "/do-not-call",
        param: ["workspace"],
        // routeRole owner, admin, member, campaign_manager — exactly `do_not_call_entry.add`'s roles.
        capability: "do_not_call_entry.add",
        requiresSignIn: true,
        screenName: { en: "Do not call", el: "Μην καλείτε" },
    },
    "test_console": {
        path: "/test-console",
        param: ["workspace"],
        // routeRole owner, admin, member, developer — exactly `voice_agent.write`'s roles.
        capability: "voice_agent.write",
        requiresSignIn: true,
        screenName: { en: "Test console", el: "Κονσόλα δοκιμών" },
    },
    "docs": {
        path: "/docs",
        param: ["workspace"],
        capability: null,
        requiresSignIn: true,
        screenName: { en: "Documentation", el: "Τεκμηρίωση" },
    },
    "billing": {
        path: "/billing",
        param: ["workspace"],
        capability: "billing.read",
        requiresSignIn: true,
        screenName: { en: "Billing", el: "Χρέωση" },
    },
    "billing.ledger": {
        path: "/billing/ledger",
        param: ["workspace"],
        capability: "billing.read",
        requiresSignIn: true,
        screenName: { en: "Transactions", el: "Συναλλαγές" },
    },
    // ── the current organization ───────────────────────────────────────────────────────────────
    "organization": {
        path: "/organization",
        param: ["organization"],
        capability: "account.profile.read",
        requiresSignIn: true,
        screenName: { en: "Organization", el: "Οργανισμός" },
    },
    "organization.workspaces": {
        path: "/organization/workspaces",
        param: ["organization"],
        capability: "account.workspace_metadata.read",
        requiresSignIn: true,
        screenName: { en: "Workspaces", el: "Χώροι εργασίας" },
    },
    "organization.people": {
        path: "/organization/people",
        param: ["organization"],
        capability: "account_membership.read",
        requiresSignIn: true,
        screenName: { en: "People and roles", el: "Μέλη και ρόλοι" },
    },
    "organization.security": {
        path: "/organization/security",
        param: ["organization"],
        capability: "account.security.read",
        requiresSignIn: true,
        screenName: { en: "Sign-in and access", el: "Σύνδεση και πρόσβαση" },
    },
    "organization.sharing": {
        path: "/organization/sharing",
        param: ["organization"],
        capability: "account.resource_share.read",
        requiresSignIn: true,
        screenName: { en: "Sharing between workspaces", el: "Κοινή χρήση μεταξύ χώρων εργασίας" },
    },
    "organization.archive_close": {
        path: "/organization/archive-close",
        param: ["organization"],
        capability: "account.workspace.manage",
        requiresSignIn: true,
        screenName: { en: "Archive, restore, close", el: "Αρχειοθέτηση, επαναφορά, κλείσιμο" },
    },
    "audit": {
        path: "/audit",
        param: ["organization"],
        // The sidebar's organization row (`accountOnly`); with a workspace open the log is reached through Compliance.
        capability: "account.audit.financial_read",
        requiresSignIn: true,
        screenName: { en: "Audit log", el: "Αρχείο ελέγχου" },
    },
    "finance.funding": {
        path: "/finance/funding",
        param: ["organization"],
        capability: "account.finance.read",
        requiresSignIn: true,
        screenName: { en: "Balances and funding", el: "Υπόλοιπα και χρηματοδότηση" },
    },
    "finance.plans": {
        path: "/finance/plans",
        param: ["organization"],
        capability: "account.finance.read",
        requiresSignIn: true,
        screenName: { en: "Plans", el: "Προγράμματα" },
    },
    "finance.allowances": {
        path: "/finance/allowances",
        param: ["organization"],
        capability: "account.finance.read",
        requiresSignIn: true,
        screenName: { en: "Allowances and spend caps", el: "Παροχές και όρια δαπανών" },
    },
    "finance.documents": {
        path: "/finance/documents",
        param: ["organization"],
        capability: "account.finance.read",
        requiresSignIn: true,
        screenName: { en: "Invoices, receipts and statements", el: "Τιμολόγια, αποδείξεις και καταστάσεις" },
    },
    "finance.postpaid": {
        path: "/finance/postpaid",
        param: ["organization"],
        capability: "account.finance.read",
        requiresSignIn: true,
        screenName: { en: "Postpaid", el: "Μεταπληρωμή" },
    },
    // ── the person's own pages ─────────────────────────────────────────────────────────────────
    "me.security": {
        path: "/me/security",
        param: [],
        capability: null,
        requiresSignIn: true,
        screenName: { en: "Your sign-in and devices", el: "Η σύνδεση και οι συσκευές σας" },
    },
    "start.organization": {
        path: "/start/organization",
        param: [],
        capability: null,
        requiresSignIn: true,
        screenName: { en: "Create an organization", el: "Δημιουργία οργανισμού" },
    },
    // ── moves within the page the person is on (owner request 2026-10-17) ──────────────────────
    // The new-agent wizard's steps (`wizardStepsOf`: Content, or Menu · Hours · Orders, then Languages, Channels, Review).
    "agent_setup.content": voiceAgentSetupStep("content", { en: "Content", el: "Περιεχόμενο" }),
    "agent_setup.menu": voiceAgentSetupStep("menu", { en: "Menu", el: "Μενού" }),
    "agent_setup.hours": voiceAgentSetupStep("hours", { en: "Hours", el: "Ωράριο" }),
    "agent_setup.orders": voiceAgentSetupStep("orders", { en: "Orders", el: "Παραγγελίες" }),
    "agent_setup.language": voiceAgentSetupStep("language", { en: "Languages", el: "Γλώσσες" }),
    "agent_setup.medium": voiceAgentSetupStep("medium", { en: "Channels", el: "Κανάλια" }),
    "agent_setup.summary": voiceAgentSetupStep("summary", { en: "Review", el: "Έλεγχος" }),
    // An agent's tabs (`VOICE_AGENT_TAB`).
    "agent.live": voiceAgentTab("live", { en: "Live", el: "Ενεργός" }),
    "agent.orders": voiceAgentTab("orders", { en: "Orders", el: "Παραγγελίες" }),
    "agent.review": voiceAgentTab("review", { en: "Review", el: "Έλεγχος" }),
    "agent.outbound": voiceAgentTab("outbound", { en: "Campaigns", el: "Καμπάνιες" }),
    "agent.results": voiceAgentTab("results", { en: "Results", el: "Αποτελέσματα" }),
    "agent.history": voiceAgentTab("history", { en: "History", el: "Ιστορικό" }),
    "agent.analytics": voiceAgentTab("analytics", { en: "Analytics", el: "Αναλύσεις" }),
    "agent.test": voiceAgentTab("test", { en: "Test", el: "Δοκιμή" }, "voice_agent.write"),
    "agent.channels": voiceAgentTab("channels", { en: "Channels", el: "Κανάλια" }),
    "agent.versions": voiceAgentTab("versions", { en: "Versions", el: "Εκδόσεις" }),
};
/** Every registered route id, sorted (the prompt's route block and the tool enum read this order). */
export const HELP_ROUTE_ID = Object.freeze(Object.keys(HELP_ROUTE_REGISTRY).sort((left, right) => (left < right ? -1 : left > right ? 1 : 0)));
const HELP_ROUTE_ID_SET = new Set(HELP_ROUTE_ID);
/**
 * The registered routes wetalk.io links to (`apps/web-public`'s `helpPublicLinkOf`): the two way-in pages, and `docs`,
 * which on the public site is the public documentation (`/docs/start/`, `/el/docs/start/`) and needs no sign-in there.
 * The help host shows exactly these on `public_site` and refuses every other route `other_surface`; on the portal
 * `docs` stays a signed-in workspace route (`requiresSignIn` unchanged). Sorted, as `HELP_ROUTE_ID`.
 */
export const HELP_PUBLIC_SITE_ROUTE_ID = Object.freeze(["docs", "log_in", "sign_up"]);
const HELP_PUBLIC_SITE_ROUTE_ID_SET = new Set(HELP_PUBLIC_SITE_ROUTE_ID);
/** Whether wetalk.io has a page for this registered route (a link the public site's widget may show). */
export function isHelpPublicSiteRouteId(value) {
    return typeof value === "string" && HELP_PUBLIC_SITE_ROUTE_ID_SET.has(value);
}
export function isHelpRouteId(value) {
    return typeof value === "string" && HELP_ROUTE_ID_SET.has(value);
}
/** The registered route, or `null` for anything outside the registry (refused `not_in_registry`). */
export function helpRouteOf(value) {
    return isHelpRouteId(value) ? HELP_ROUTE_REGISTRY[value] : null;
}
/** The registered move within a page, or `null` for a screen route and anything outside the registry. */
export function helpPlaceRouteOf(value) {
    const route = helpRouteOf(value);
    return route !== null && "within" in route ? route : null;
}
/** The move to `place` on the page `within`, or `null` when none is registered. */
export function helpPlaceRouteIdOf(within, place) {
    const id = `${within}.${place}`;
    return helpPlaceRouteOf(id) === null ? null : id;
}
const HELP_ROUTE_ID_BY_PATH = new Map(HELP_ROUTE_ID.filter((id) => helpPlaceRouteOf(id) === null).map((id) => [HELP_ROUTE_REGISTRY[id].path, id]));
/**
 * The registered route a portal pathname shows (OD-5's page context), or `null`. Exact match only, a trailing slash
 * aside: a record page under a registered prefix (`/agents/<id>`) is not that route.
 */
export function helpRouteIdOfPath(pathname) {
    const exact = HELP_ROUTE_ID_BY_PATH.get(pathname);
    if (exact !== undefined)
        return exact;
    if (pathname.length <= 1 || !pathname.endsWith("/"))
        return null;
    const trimmed = HELP_ROUTE_ID_BY_PATH.get(pathname.slice(0, -1));
    return trimmed === undefined ? null : trimmed;
}
/**
 * The prompt's route block (design §31.6.2 [4]): one `route_id — screen name (en / el)` line per registered route, in
 * id order. Identical for every conversation, so it sits in the cached prefix.
 */
export function helpRouteLines() {
    return HELP_ROUTE_ID.map((id) => {
        const name = HELP_ROUTE_REGISTRY[id].screenName;
        return `${id} — ${name.en} / ${name.el}`;
    });
}
//# sourceMappingURL=route-registry.js.map